<template>
    <div
        class="mx-4"
    >
        <h3
            class="py-0 mt-0 mb-4 text-uppercase text-center text-md-start"
        >
            Product Comparison
        </h3>

        <!-- Spinner -->
        <div
            v-if="['idle', 'pending'].includes(compGroupsLoadingStatus)"
            class="d-flex align-center justify-center"
        >
            <v-progress-circular
                size="42"
                color="accent"
                indeterminate
            ></v-progress-circular>
        </div>
        <template
            v-else-if="!lo_isError(compGroupsData) && !!compGroupsData?.length"
        >
            <ComparisonGroupsTables
                :model-value="compGroupsData"
                :product-uid="productUid"
            ></ComparisonGroupsTables>
        </template>
        <template
            v-else-if="!lo_isError(compGroupsData) && !compGroupsData?.length"
        >
            No Product Comparisons found.
        </template>
        <template
            v-else
        >
            <v-alert
                type="warning"
            >
                Unable to load Product Comparisons. Please reload page to try again.
            </v-alert>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type {
        ProductComparisonGroup
    } from '~/types';
    import type {
        PaginatedRequest
    } from 'x1-base/types';
    import type { NuxtError } from '#app';
    import {
        useApiQueryParams,
        useProductComparisonGroups
    } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import ComparisonGroupsTables from '~/components/products/product/comparison-groups/ComparisonGroupsTables/ComparisonGroupsTables.vue';

    const props = defineProps<{
        productUid: string;
    }>();

    const emit = defineEmits<{
        'on-no-data': [value: void];
    }>();

    const {
        updatePagination,
        getApiQueryParams
    } = useApiQueryParams({
        key: 'ProdCompGroupsQParams',
        resetOnUnload: true,
        opts: {
            page_size: 22,
        },
        persistedParams: {
            product: props.productUid
        }
    });

    const { getComparisonGroups } = useProductComparisonGroups();

    async function getItems(): Promise<ProductComparisonGroup[] | NuxtError> {
        const { productUid } = props;

        const paginatedRequest: PaginatedRequest = {
            params: getApiQueryParams()
        };

        const response = await getComparisonGroups(paginatedRequest);

        if (lo_isError(response)) {
            return response;
        }

        if (!response.data.length) {
            emit('on-no-data');
        }

        updatePagination(response.pagination);
        return response.data;
    }

    const {
        data: compGroupsData,
        refresh: onReloadList,
        status: compGroupsLoadingStatus
    } = useLazyAsyncData<ProductComparisonGroup[] | NuxtError>(
        `keyCompGroupItems`,
        () => getItems(),
        {
            server: false
        }
    );
</script>

<style lang="scss"></style>
